import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeftIcon,
  ChartBarIcon,
  UserIcon,
  DevicePhoneMobileIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api.ts';
import { LanguageSwitcher } from '../components/LanguageSwitcher.tsx';

interface SessionDetailModel {
  session_id: string;
  device_id: string;
  device_name: string;
  start_time: string;
  end_time: string;
  duration_seconds: number;
  total_analyses: number;
  overall_score: number;
  confidence: number;
  posture_metrics: {
    neck_angle: number;
    shoulder_alignment: number;
    spine_curvature: number;
    hip_alignment: number;
  };
  alerts: Array<{
    type: string;
    severity: string;
    message: string;
    recommendation: string;
  }>;
  recommendations: string[];
  qr_code_data?: {
    qr_code_path: string;
    download_url: string;
  };
  location?: {
    name: string;
    address: string;
  };
  geo_location?: {
    latitude: number;
    longitude: number;
    city: string;
    country: string;
  };
}

interface AnalysisData {
  timestamp: string;
  frame_number: number;
  overall_score: number;
  confidence: number;
  neck_angle: number;
  shoulder_alignment: number;
  spine_curvature: number;
  hip_alignment: number;
}

function SessionDetails() {
  const { t, i18n } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionDetailModel | null>(null);
  const [analyses, setAnalyses] = useState<AnalysisData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionId) {
      fetchSessionDetails();
      fetchSessionAnalyses();
    }
  }, [sessionId]);

  const fetchSessionDetails = async () => {
    try {
      setLoading(true);
      const sessionData = await apiService.getSessionDetails(sessionId!);
      setSession(sessionData);
    } catch (err) {
      setError(i18n.t('sessionDetails.fetchFailed'));
      console.error('Error fetching session details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionAnalyses = async () => {
    try {
      const analysesData = await apiService.getSessionAnalyses(sessionId!);
      setAnalyses(analysesData);
    } catch (err) {
      console.error('Error fetching session analyses:', err);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 75) return 'text-green-400';
    return 'text-yellow-400';
  };

  // Unified color thresholds for posture metrics (consistent with PDF and native app)
  // Green (#10B981): Good alignment
  // Yellow (#F59E0B): Moderate deviation, needs attention
  // Red (#EF4444): Significant deviation, requires correction
  const getMetricColor = (value: number, metricType: 'shoulder' | 'neck' | 'spine' | 'hip') => {
    const absValue = Math.abs(value);
    
    // Shoulder/Hip alignment: ideal=0°, green≤3°, yellow≤6°, red>6°
    if (metricType === 'shoulder' || metricType === 'hip') {
      if (absValue <= 3) return '#10B981'; // green
      if (absValue <= 6) return '#F59E0B'; // yellow
      return '#EF4444'; // red
    }
    
    // Neck/Spine angles: ideal=0°, green≤5°, yellow≤10°, red>10°
    if (metricType === 'neck' || metricType === 'spine') {
      if (absValue <= 5) return '#10B981'; // green
      if (absValue <= 10) return '#F59E0B'; // yellow
      return '#EF4444'; // red
    }
    
    return '#10B981'; // default green
  };

  if (loading) {
    return (
      <div className="relative min-h-screen bg-regina-gray-50 flex items-center justify-center">
        <div className="fixed top-3 right-3 z-50">
          <LanguageSwitcher />
        </div>
        <div className="text-regina-gray-800 text-xl">{t('sessionDetails.loading')}</div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="relative min-h-screen bg-regina-gray-50 flex items-center justify-center">
        <div className="fixed top-3 right-3 z-50">
          <LanguageSwitcher />
        </div>
        <div className="text-center">
          <div className="text-regina-error text-xl mb-4">{t('sessionDetails.loadErrorTitle')}</div>
          <button
            onClick={() => navigate('/history')}
            className="px-4 py-2 bg-regina-red text-white rounded-lg hover:bg-red-700"
          >
            {t('sessionDetails.backToHistory')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-regina-gray-50">
      <div className="fixed top-3 right-3 z-50">
        <LanguageSwitcher />
      </div>
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            type="button"
            onClick={() => navigate('/history')}
            className="mb-4 flex items-center font-medium text-regina-gray-600 transition-colors hover:text-regina-red"
          >
            <ArrowLeftIcon className="mr-2 h-5 w-5" />
            {t('sessionDetails.backToHistory')}
          </button>
          
          {/* Unified Session Info Card */}
          <div className="rounded-xl border border-regina-gray-100 bg-white p-4 shadow-sm sm:p-6">
            {/* Title and Score Row */}
            <div className="mb-4 flex flex-col gap-4 border-b border-regina-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-regina-gray-800 sm:text-2xl">{t('sessionDetails.title')}</h1>
                <p className="mt-1 break-all text-sm text-regina-gray-600">
                  {t('sessionDetails.sessionIdLabel')} {session.session_id}
                </p>
              </div>
              
              <div className="text-left sm:text-right">
                <div className={`text-3xl font-bold sm:text-4xl ${getScoreColor(session.overall_score)}`}>
                  {session.overall_score.toFixed(1)}
                </div>
                <div className="text-sm font-medium text-regina-gray-600">{t('sessionDetails.overallScore')}</div>
              </div>
            </div>
            
            {/* Session Info Row */}
            <div className="flex flex-col flex-wrap gap-4 text-sm sm:flex-row sm:items-center sm:gap-6">
              <div className="flex items-center space-x-2">
                <DevicePhoneMobileIcon className="w-4 h-4 text-regina-red" />
                <span className="text-regina-gray-600">{t('sessionDetails.device')}</span>
                <span className="text-regina-gray-800 font-semibold">{session.device_name}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <ChartBarIcon className="w-4 h-4 text-regina-red" />
                <span className="text-regina-gray-600">{t('sessionDetails.analyses')}</span>
                <span className="text-regina-gray-800 font-semibold">{session.total_analyses}</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <UserIcon className="w-4 h-4 text-regina-red" />
                <span className="text-regina-gray-600">{t('sessionDetails.accuracy')}</span>
                <span className="text-regina-gray-800 font-semibold">{(session.confidence * 100).toFixed(1)}%</span>
              </div>
              
              {session.location && (session.location.name || session.location.address) && (
                <div className="flex items-center space-x-2">
                  <MapPinIcon className="w-4 h-4 text-regina-red" />
                  <span className="text-regina-gray-600">{t('sessionDetails.location')}</span>
                  <span className="text-regina-gray-800 font-semibold">
                    {[session.location.name, session.location.address].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Posture Visualization */}
          <div className="bg-white rounded-xl p-6 border border-regina-gray-100 mb-8 shadow-sm">
          <h3 className="text-lg font-semibold text-regina-gray-800 mb-6">{t('sessionDetails.postureAnalysis')}</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Front View */}
            <div className="flex flex-col items-center">
              <h4 className="text-regina-gray-800 font-semibold mb-4">{t('sessionDetails.viewFront')}</h4>
              <div className="relative w-full max-w-[16rem] mx-auto sm:mx-0 h-72 sm:h-80 bg-gradient-to-b from-regina-gray-50 to-regina-gray-100 rounded-xl p-4 sm:p-6 border border-regina-gray-200 mb-4">
                <svg viewBox="0 0 120 180" className="w-full h-full">
                  {/* Head with details */}
                  <ellipse cx="60" cy="18" rx="14" ry="16" fill="#94A3B8" />
                  <ellipse cx="60" cy="20" rx="12" ry="14" fill="#64748B" opacity="0.8" />
                  
                  {/* Neck muscles - Sternocleidomastoid */}
                  <ellipse cx="55" cy="38" rx="3" ry="8" fill={getMetricColor(session.posture_metrics.neck_angle, 'neck')} opacity="0.7" />
                  <ellipse cx="65" cy="38" rx="3" ry="8" fill={getMetricColor(session.posture_metrics.neck_angle, 'neck')} opacity="0.7" />
                  <rect x="56" y="32" width="8" height="12" fill={getMetricColor(session.posture_metrics.neck_angle, 'neck')} opacity="0.5" rx="2" />
                  
                  {/* Trapezius muscles */}
                  <path d="M 35 50 Q 60 45, 85 50 L 80 60 Q 60 55, 40 60 Z" 
                        fill={getMetricColor(session.posture_metrics.shoulder_alignment, 'shoulder')} opacity="0.6" />
                  
                  {/* Shoulders - Deltoids */}
                  <ellipse cx="35" cy="58" rx="12" ry="14" fill="#7C8B9F" opacity="0.7" />
                  <ellipse cx="85" cy="58" rx="12" ry="14" fill="#7C8B9F" opacity="0.7" />
                  <line x1="35" y1="50" x2="85" y2="50" 
                        stroke={getMetricColor(session.posture_metrics.shoulder_alignment, 'shoulder')} 
                        strokeWidth="3" strokeLinecap="round" />
                  
                  {/* Chest - Pectoralis muscles */}
                  <ellipse cx="50" cy="68" rx="10" ry="12" fill="#8B9AAE" opacity="0.5" />
                  <ellipse cx="70" cy="68" rx="10" ry="12" fill="#8B9AAE" opacity="0.5" />
                  
                  {/* Spine/Torso center */}
                  <rect x="57" y="50" width="6" height="60" 
                        fill={getMetricColor(session.posture_metrics.spine_curvature, 'spine')} opacity="0.7" rx="3" />
                  
                  {/* Abdominal muscles - Rectus Abdominis */}
                  <rect x="52" y="70" width="7" height="10" fill="#6B7A8E" opacity="0.4" rx="2" />
                  <rect x="61" y="70" width="7" height="10" fill="#6B7A8E" opacity="0.4" rx="2" />
                  <rect x="52" y="82" width="7" height="10" fill="#6B7A8E" opacity="0.4" rx="2" />
                  <rect x="61" y="82" width="7" height="10" fill="#6B7A8E" opacity="0.4" rx="2" />
                  <rect x="52" y="94" width="7" height="10" fill="#6B7A8E" opacity="0.4" rx="2" />
                  <rect x="61" y="94" width="7" height="10" fill="#6B7A8E" opacity="0.4" rx="2" />
                  
                  {/* Arms - Biceps/Triceps */}
                  <ellipse cx="28" cy="75" rx="6" ry="18" fill="#7C8B9F" opacity="0.6" />
                  <ellipse cx="92" cy="75" rx="6" ry="18" fill="#7C8B9F" opacity="0.6" />
                  <ellipse cx="25" cy="100" rx="5" ry="15" fill="#6B7A8E" opacity="0.5" />
                  <ellipse cx="95" cy="100" rx="5" ry="15" fill="#6B7A8E" opacity="0.5" />
                  
                  {/* Hips - Iliac crest */}
                  <ellipse cx="50" cy="112" rx="15" ry="8" fill={getMetricColor(session.posture_metrics.hip_alignment, 'hip')} opacity="0.6" />
                  <ellipse cx="70" cy="112" rx="15" ry="8" fill={getMetricColor(session.posture_metrics.hip_alignment, 'hip')} opacity="0.6" />
                  <line x1="43" y1="112" x2="77" y2="112" 
                        stroke={getMetricColor(session.posture_metrics.hip_alignment, 'hip')} 
                        strokeWidth="4" strokeLinecap="round" />
                  
                  {/* Legs - Quadriceps */}
                  <ellipse cx="52" cy="135" rx="6" ry="20" fill="#7C8B9F" opacity="0.6" />
                  <ellipse cx="68" cy="135" rx="6" ry="20" fill="#7C8B9F" opacity="0.6" />
                  <ellipse cx="52" cy="165" rx="5" ry="12" fill="#6B7A8E" opacity="0.5" />
                  <ellipse cx="68" cy="165" rx="5" ry="12" fill="#6B7A8E" opacity="0.5" />
                </svg>
              </div>
              <div className="bg-regina-gray-50 rounded-lg p-6 w-full space-y-4 border border-regina-gray-200">
                <div className="text-center">
                  <div className="text-regina-gray-600 text-sm mb-2">{t('sessionDetails.shoulderAlignment')}</div>
                  <div className="text-regina-gray-800 text-3xl font-bold mb-1">{session.posture_metrics.shoulder_alignment.toFixed(1)}°</div>
                  <div className="w-full h-2 rounded-full overflow-hidden" 
                       style={{ backgroundColor: `${getMetricColor(session.posture_metrics.shoulder_alignment, 'shoulder')}40` }}>
                    <div className="h-full" 
                         style={{ 
                           backgroundColor: getMetricColor(session.posture_metrics.shoulder_alignment, 'shoulder'),
                           width: '100%'
                         }}></div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-regina-gray-600 text-sm mb-2">{t('sessionDetails.hipAlignment')}</div>
                  <div className="text-regina-gray-800 text-3xl font-bold mb-1">{session.posture_metrics.hip_alignment.toFixed(1)}°</div>
                  <div className="w-full h-2 rounded-full overflow-hidden"
                       style={{ backgroundColor: `${getMetricColor(session.posture_metrics.hip_alignment, 'hip')}40` }}>
                    <div className="h-full"
                         style={{ 
                           backgroundColor: getMetricColor(session.posture_metrics.hip_alignment, 'hip'),
                           width: '100%'
                         }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Side View */}
            <div className="flex flex-col items-center">
              <h4 className="text-regina-gray-800 font-semibold mb-4">{t('sessionDetails.viewSide')}</h4>
              <div className="relative w-full max-w-[16rem] mx-auto sm:mx-0 h-72 sm:h-80 bg-gradient-to-b from-regina-gray-50 to-regina-gray-100 rounded-xl p-4 sm:p-6 border border-regina-gray-200 mb-4">
                <svg viewBox="0 0 120 180" className="w-full h-full">
                  {/* Head profile */}
                  <ellipse cx="65" cy="18" rx="14" ry="16" fill="#94A3B8" />
                  <path d="M 70 18 Q 75 22, 72 28" stroke="#64748B" strokeWidth="2" fill="none" />
                  
                  {/* Neck - with forward head posture consideration */}
                  <path d={`M 62 32 Q ${62 + session.posture_metrics.neck_angle * 0.4} 38, ${60 + session.posture_metrics.neck_angle * 0.3} 48`}
                        fill={getMetricColor(session.posture_metrics.neck_angle, 'neck')} opacity="0.7" stroke="none" />
                  <ellipse cx={60 + session.posture_metrics.neck_angle * 0.2} cy="42" rx="7" ry="12" 
                           fill={getMetricColor(session.posture_metrics.neck_angle, 'neck')} opacity="0.6" />
                  
                  {/* Trapezius side view */}
                  <path d={`M ${60 + session.posture_metrics.neck_angle * 0.3} 48 L 55 58 L 58 50 Z`}
                        fill="#7C8B9F" opacity="0.6" />
                  
                  {/* Spine curvature - cervical, thoracic, lumbar */}
                  <path d={`M ${60 + session.posture_metrics.neck_angle * 0.3} 48 
                           Q ${58 + session.posture_metrics.spine_curvature * 0.35} 70, 
                             ${60 + session.posture_metrics.spine_curvature * 0.25} 90
                           Q ${62 + session.posture_metrics.spine_curvature * 0.15} 100, 60 112`}
                        stroke={getMetricColor(session.posture_metrics.spine_curvature, 'spine')} 
                        strokeWidth="5" fill="none" strokeLinecap="round" />
                  
                  {/* Chest/Pectoralis side view */}
                  <ellipse cx="70" cy="68" rx="18" ry="14" fill="#8B9AAE" opacity="0.5" />
                  
                  {/* Abdominal wall */}
                  <path d="M 65 75 Q 68 90, 65 105" stroke="#6B7A8E" strokeWidth="10" opacity="0.4" strokeLinecap="round" />
                  
                  {/* Back muscles - Latissimus dorsi, Erector spinae */}
                  <ellipse cx="52" cy="75" rx="10" ry="25" fill="#7C8B9F" opacity="0.6" />
                  <ellipse cx="55" cy="95" rx="8" ry="15" fill="#6B7A8E" opacity="0.5" />
                  
                  {/* Shoulder/Deltoid side */}
                  <ellipse cx="60" cy="55" rx="14" ry="12" fill="#7C8B9F" opacity="0.7" />
                  
                  {/* Arm */}
                  <ellipse cx="55" cy="80" rx="8" ry="20" fill="#7C8B9F" opacity="0.5" transform="rotate(-10 55 80)" />
                  
                  {/* Gluteus */}
                  <ellipse cx="58" cy="115" rx="14" ry="12" fill="#7C8B9F" opacity="0.6" />
                  <ellipse cx="60" cy="112" rx="10" ry="8" 
                           fill={getMetricColor(session.posture_metrics.hip_alignment, 'hip')} opacity="0.7" />
                  
                  {/* Hamstrings and Quadriceps */}
                  <ellipse cx="62" cy="140" rx="9" ry="22" fill="#7C8B9F" opacity="0.6" />
                  <ellipse cx="58" cy="140" rx="8" ry="22" fill="#6B7A8E" opacity="0.5" />
                  <ellipse cx="60" cy="168" rx="7" ry="12" fill="#6B7A8E" opacity="0.5" />
                </svg>
              </div>
              <div className="bg-regina-gray-50 rounded-lg p-6 w-full space-y-4 border border-regina-gray-200">
                <div className="text-center">
                  <div className="text-regina-gray-600 text-sm mb-2">{t('sessionDetails.neckAngle')}</div>
                  <div className="text-regina-gray-800 text-3xl font-bold mb-1">{session.posture_metrics.neck_angle.toFixed(1)}°</div>
                  <div className="w-full h-2 rounded-full overflow-hidden"
                       style={{ backgroundColor: `${getMetricColor(session.posture_metrics.neck_angle, 'neck')}40` }}>
                    <div className="h-full"
                         style={{ 
                           backgroundColor: getMetricColor(session.posture_metrics.neck_angle, 'neck'),
                           width: '100%'
                         }}></div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-regina-gray-600 text-sm mb-2">{t('sessionDetails.spineCurvature')}</div>
                  <div className="text-regina-gray-800 text-3xl font-bold mb-1">{session.posture_metrics.spine_curvature.toFixed(1)}°</div>
                  <div className="w-full h-2 rounded-full overflow-hidden"
                       style={{ backgroundColor: `${getMetricColor(session.posture_metrics.spine_curvature, 'spine')}40` }}>
                    <div className="h-full"
                         style={{ 
                           backgroundColor: getMetricColor(session.posture_metrics.spine_curvature, 'spine'),
                           width: '100%'
                         }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Back View */}
            <div className="flex flex-col items-center">
              <h4 className="text-regina-gray-800 font-semibold mb-4">{t('sessionDetails.viewBack')}</h4>
              <div className="relative w-full max-w-[16rem] mx-auto sm:mx-0 h-72 sm:h-80 bg-gradient-to-b from-regina-gray-50 to-regina-gray-100 rounded-xl p-4 sm:p-6 border border-regina-gray-200 mb-4">
                <svg viewBox="0 0 120 180" className="w-full h-full">
                  {/* Head back */}
                  <ellipse cx="60" cy="18" rx="14" ry="16" fill="#94A3B8" />
                  <ellipse cx="60" cy="20" rx="11" ry="13" fill="#64748B" opacity="0.6" />
                  
                  {/* Neck back muscles */}
                  <rect x="55" y="28" width="10" height="16" fill={getMetricColor(session.posture_metrics.neck_angle, 'neck')} opacity="0.7" rx="3" />
                  
                  {/* Trapezius - upper, middle, lower */}
                  <path d="M 30 48 Q 60 42, 90 48 L 85 65 Q 60 58, 35 65 Z"
                        fill={getMetricColor(session.posture_metrics.shoulder_alignment, 'shoulder')} opacity="0.7" />
                  <path d="M 35 65 Q 60 60, 85 65 L 80 82 Q 60 76, 40 82 Z"
                        fill="#7C8B9F" opacity="0.6" />
                  
                  {/* Shoulder line */}
                  <line x1="32" y1="52" x2="88" y2="52"
                        stroke={getMetricColor(session.posture_metrics.shoulder_alignment, 'shoulder')} 
                        strokeWidth="4" strokeLinecap="round" />
                  
                  {/* Deltoids back */}
                  <ellipse cx="32" cy="58" rx="13" ry="15" fill="#7C8B9F" opacity="0.7" />
                  <ellipse cx="88" cy="58" rx="13" ry="15" fill="#7C8B9F" opacity="0.7" />
                  
                  {/* Scapula (shoulder blades) */}
                  <path d="M 42 55 L 42 75 L 50 70 L 48 58 Z" fill="#5A6B7F" opacity="0.7" stroke="#4A5B6F" strokeWidth="1" />
                  <path d="M 78 55 L 78 75 L 70 70 L 72 58 Z" fill="#5A6B7F" opacity="0.7" stroke="#4A5B6F" strokeWidth="1" />
                  
                  {/* Erector spinae (spinal muscles) */}
                  <ellipse cx="54" cy="75" rx="4" ry="30" fill={getMetricColor(session.posture_metrics.spine_curvature, 'spine')} opacity="0.6" />
                  <ellipse cx="66" cy="75" rx="4" ry="30" fill={getMetricColor(session.posture_metrics.spine_curvature, 'spine')} opacity="0.6" />
                  <rect x="57" y="50" width="6" height="60" fill={getMetricColor(session.posture_metrics.spine_curvature, 'spine')} opacity="0.8" rx="3" />
                  
                  {/* Latissimus dorsi */}
                  <path d="M 40 80 Q 50 85, 60 88 Q 70 85, 80 80 L 75 100 Q 60 105, 45 100 Z"
                        fill="#6B7A8E" opacity="0.6" />
                  
                  {/* Lower back muscles */}
                  <ellipse cx="54" cy="102" rx="5" ry="12" fill="#7C8B9F" opacity="0.5" />
                  <ellipse cx="66" cy="102" rx="5" ry="12" fill="#7C8B9F" opacity="0.5" />
                  
                  {/* Arms - Triceps back view */}
                  <ellipse cx="22" cy="75" rx="7" ry="20" fill="#7C8B9F" opacity="0.6" />
                  <ellipse cx="98" cy="75" rx="7" ry="20" fill="#7C8B9F" opacity="0.6" />
                  <ellipse cx="20" cy="100" rx="6" ry="16" fill="#6B7A8E" opacity="0.5" />
                  <ellipse cx="100" cy="100" rx="6" ry="16" fill="#6B7A8E" opacity="0.5" />
                  
                  {/* Gluteus maximus */}
                  <ellipse cx="52" cy="118" rx="14" ry="12" fill="#7C8B9F" opacity="0.7" />
                  <ellipse cx="68" cy="118" rx="14" ry="12" fill="#7C8B9F" opacity="0.7" />
                  
                  {/* Hip alignment indicator */}
                  <line x1="42" y1="115" x2="78" y2="115"
                        stroke={getMetricColor(session.posture_metrics.hip_alignment, 'hip')} 
                        strokeWidth="4" strokeLinecap="round" />
                  
                  {/* Hamstrings */}
                  <ellipse cx="52" cy="142" rx="7" ry="22" fill="#7C8B9F" opacity="0.6" />
                  <ellipse cx="68" cy="142" rx="7" ry="22" fill="#7C8B9F" opacity="0.6" />
                  
                  {/* Calves */}
                  <ellipse cx="52" cy="168" rx="6" ry="12" fill="#6B7A8E" opacity="0.6" />
                  <ellipse cx="68" cy="168" rx="6" ry="12" fill="#6B7A8E" opacity="0.6" />
                </svg>
              </div>
              <div className="bg-regina-gray-50 rounded-lg p-6 w-full space-y-4 border border-regina-gray-200">
                <div className="text-center">
                  <div className="text-regina-gray-600 text-sm mb-2">{t('sessionDetails.spineAlignment')}</div>
                  <div className="text-regina-gray-800 text-3xl font-bold mb-1">{session.posture_metrics.spine_curvature.toFixed(1)}°</div>
                  <div className="w-full h-2 rounded-full overflow-hidden"
                       style={{ backgroundColor: `${getMetricColor(session.posture_metrics.spine_curvature, 'spine')}40` }}>
                    <div className="h-full"
                         style={{ 
                           backgroundColor: getMetricColor(session.posture_metrics.spine_curvature, 'spine'),
                           width: '100%'
                         }}></div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-regina-gray-600 text-sm mb-2">{t('sessionDetails.shoulderBalance')}</div>
                  <div className="text-regina-gray-800 text-3xl font-bold mb-1">{session.posture_metrics.shoulder_alignment.toFixed(1)}°</div>
                  <div className="w-full h-2 rounded-full overflow-hidden"
                       style={{ backgroundColor: `${getMetricColor(session.posture_metrics.shoulder_alignment, 'shoulder')}40` }}>
                    <div className="h-full"
                         style={{ 
                           backgroundColor: getMetricColor(session.posture_metrics.shoulder_alignment, 'shoulder'),
                           width: '100%'
                         }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-8 pt-6 border-t border-regina-gray-200">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                <span className="text-regina-gray-700">{t('sessionDetails.legendOptimal')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                <span className="text-regina-gray-700">{t('sessionDetails.legendMinor')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded-full bg-red-500"></div>
                <span className="text-regina-gray-700">{t('sessionDetails.legendNeedsCorrection')}</span>
              </div>
            </div>
            <p className="text-center text-regina-gray-600 text-xs mt-4">
              {t('sessionDetails.legendFootnote')}
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}

export default SessionDetails;
